// WordCloudViewer.js
import React, { useState, useEffect, useRef } from 'react';
import ReactWordcloud from 'react-wordcloud';
import './GridAnalytics.css';
import './AnalyticsComponents.css';
import { surveyAPI } from '../../services/apiClient';

const PAGE_SIZE = 5;
const PDF_RESPONSE_LIMIT = 5;
const FETCH_BATCH_SIZE = 50;

const normalizeResponses = (list = [], offset = 0) =>
  list
    .map((resp, idx) => {
      const text = resp.response_text ?? resp.text ?? '';
      if (!text) return null;
      return {
        id: resp.response_id ?? resp.id ?? `${resp.submission_id || 'resp'}-${offset + idx}`,
        text,
        createdAt: resp.created_at ?? resp.createdAt ?? resp.timestamp ?? null,
        userName: resp.user_name ?? resp.username ?? resp.user ?? resp.name ?? resp.userName ?? 'Anonymous',
      };
    })
    .filter(Boolean);

const normalizeWordCloud = (items = []) =>
  (items || [])
    .map((item) => ({
      text: item.text ?? item.word ?? '',
      value: item.value ?? item.count ?? 0,
    }))
    .filter((item) => item.text && item.value > 0);

const buildFallbackFromAnalytics = (analyticsData) => {
  const analytics = analyticsData?.analytics || analyticsData;
  if (!analytics) return null;
  const responsesRaw = analytics.all_responses || analytics.latest_10 || [];
  const wordCloudRaw = analytics.word_cloud_data || analytics.word_frequencies || [];
  const total =
    analytics.response_count ?? analytics.count_valid ?? responsesRaw.length;

  return {
    responses: normalizeResponses(responsesRaw, 0),
    word_cloud_data: normalizeWordCloud(wordCloudRaw),
    total_responses: total,
    has_more: false,
    offset: 0,
  };
};

const WordCloudViewer = ({ surveyId, questionId, analyticsData }) => {
  const [responses, setResponses] = useState([]);
  const [wordCloudData, setWordCloudData] = useState([]);
  const [totalResponses, setTotalResponses] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [isPdfMode, setIsPdfMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [usingFallback, setUsingFallback] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    try {
      const el = rootRef.current;
      if (el) {
        let p = el.parentElement;
        let found = false;
        let guard = 0;
        while (p && guard < 20) {
          if (p.classList && p.classList.contains('pdf-card')) {
            found = true;
            break;
          }
          p = p.parentElement;
          guard++;
        }
        setIsPdfMode(found);
      }
    } catch {
      // ignore DOM inspection errors
    }
  }, []);

  const hydrateFromPayload = (payload, append = false) => {
    const normalizedResponses = normalizeResponses(
      payload.responses || [],
      append ? responses.length : 0
    );
    const combinedResponses = append
      ? [...responses, ...normalizedResponses]
      : normalizedResponses;

    const total =
      payload.total_responses ??
      payload.totalResponses ??
      payload.total ??
      totalResponses ??
      combinedResponses.length;
    const offsetVal = payload.offset ?? (append ? responses.length : 0);
    const hasMoreFlag =
      payload.has_more ??
      (total ? offsetVal + normalizedResponses.length < total : false);

    const normalizedCloud = normalizeWordCloud(
      payload.word_cloud_data ||
        payload.wordCloudData ||
        payload.word_frequencies ||
        []
    );

    if (normalizedCloud.length) {
      setWordCloudData(normalizedCloud);
    }

    setResponses(combinedResponses);
    setTotalResponses(total);
    setHasMore(hasMoreFlag);
    setNextOffset(offsetVal + normalizedResponses.length);
  };

  const fetchBatch = async ({ offset = 0, limit = FETCH_BATCH_SIZE, append = false } = {}) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const response = await surveyAPI.getOpenEndedWithUsers(
        surveyId,
        questionId,
        limit,
        offset
      );
      hydrateFromPayload(response.data, append);
      setError('');
      setUsingFallback(false);
    } catch (err) {
      console.error('Error fetching open-ended responses:', err);
      const friendly =
        err?.response?.data?.error || err?.message || 'Unable to load responses';

      const fallbackData = buildFallbackFromAnalytics(analyticsData);
      if (!append && fallbackData) {
        hydrateFromPayload(fallbackData, false);
        setUsingFallback(true);
        setError(`Showing cached analytics data. Live fetch failed: ${friendly}`);
      } else {
        setError(friendly);
      }
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  };

  useEffect(() => {
    if (!surveyId || !questionId) return;
    setResponses([]);
    setWordCloudData([]);
    setTotalResponses(0);
    setHasMore(false);
    setNextOffset(0);
    setCurrentPage(1);
    fetchBatch({ offset: 0, limit: FETCH_BATCH_SIZE, append: false });
  }, [surveyId, questionId]);

  const formatResponseText = (text, wordsPerLine = 8) => {
    if (!text || typeof text !== 'string') return '';
    const words = text.trim().split(/\s+/);
    const lines = [];
    for (let i = 0; i < words.length; i += wordsPerLine) {
      lines.push(words.slice(i, i + wordsPerLine).join(' '));
    }
    return lines.join('\n');
  };

  const handleNextPage = async () => {
    if (loadingMore) return;
    const totalPages = Math.max(1, Math.ceil(responses.length / PAGE_SIZE));
    const isLastPage = currentPage >= totalPages;
    if (isLastPage && hasMore) {
      await fetchBatch({ offset: nextOffset, limit: FETCH_BATCH_SIZE, append: true });
    }
    setCurrentPage((prev) => prev + 1);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const wordcloudOptions = {
    rotations: 2,
    rotationAngles: [0, 90],
    fontSizes: [15, 60],
    fontFamily: 'Arial',
    colors: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b'],
    enableTooltip: true,
    deterministic: true,
    padding: 2,
    fontWeight: 'bold',
  };

  const totalAvailable = totalResponses || responses.length;
  const totalPages = Math.max(1, Math.ceil(responses.length / PAGE_SIZE));
  const pageStartIndex = (currentPage - 1) * PAGE_SIZE;
  const pageEndIndex = pageStartIndex + PAGE_SIZE;

  const displayedResponses = isPdfMode
    ? responses.slice(0, PDF_RESPONSE_LIMIT)
    : responses.slice(pageStartIndex, pageEndIndex);

  const displayStart = displayedResponses.length > 0 ? pageStartIndex + 1 : 0;
  const displayEnd = pageStartIndex + displayedResponses.length;

  useEffect(() => {
    if (isPdfMode) return;
    const maxPages = Math.max(1, Math.ceil(responses.length / PAGE_SIZE));
    if (responses.length === 0) {
      setCurrentPage(1);
    } else if (currentPage > maxPages) {
      setCurrentPage(maxPages);
    }
  }, [responses.length, currentPage, isPdfMode]);

  if (loading && responses.length === 0) {
    return (
      <div className="p-4 text-center">
        Loading open-ended responses...
      </div>
    );
  }

  if (!loading && error && responses.length === 0) {
    return (
      <div ref={rootRef}>
        <div className="open-ended-warning">
          {error}
          {!isPdfMode && (
            <div style={{ marginTop: '8px' }}>
              <button
                type="button"
                className="open-ended-see-more"
                onClick={() => fetchBatch({ offset: 0, limit: FETCH_BATCH_SIZE, append: false })}
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} data-open-ended-block>
      {(error && responses.length > 0) && (
        <div className="open-ended-warning">
          {error}
        </div>
      )}

      <div className="mb-6">
        <h4 className="text-lg font-medium mb-4">Word Cloud Analysis</h4>
        {wordCloudData.length > 0 ? (
          <div style={{
            height: isPdfMode ? '200px' : '260px',
            maxHeight: isPdfMode ? '200px' : '260px',
            width: isPdfMode ? '70%' : '100%',
            overflow: 'hidden',
            margin: isPdfMode ? '0' : undefined,
            textAlign: isPdfMode ? 'left' : 'center'
          }}>
            <ReactWordcloud words={wordCloudData} options={wordcloudOptions} />
          </div>
        ) : (
          <p className="no-data-small">Not enough text data to generate word cloud.</p>
        )}
      </div>
      
      <div className="mt-6">
        <h4 className="text-lg font-medium mb-2">Responses</h4>
        <p
          className="info-text open-ended-response-count"
          data-total={totalAvailable}
          style={{ marginTop: 0 }}
        >
          Showing {displayStart}-{displayEnd} of {totalAvailable} responses
          {isPdfMode ? ' (PDF view limited)' : ''}
          {usingFallback ? ' — using cached analytics data' : ''}
        </p>

        {responses.length > 0 ? (
          <div className="space-y-4 open-ended-responses-list" data-open-ended-responses>
            {responses.map((response, idx) => {
              const isVisible = isPdfMode
                ? idx < PDF_RESPONSE_LIMIT
                : idx >= pageStartIndex && idx < pageEndIndex;
              return (
              <div
                key={response.id}
                className="p-4 border rounded bg-gray-50 open-ended-response-card"
                data-open-ended-response
                style={isVisible ? undefined : { display: 'none' }}
              >
                <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                  {formatResponseText(response.text, 8)}
                </div>
              </div>
              );
            })}

            {!isPdfMode && (
              <div className="see-more-row open-ended-pagination">
                <button
                  type="button"
                  className="open-ended-see-more"
                  onClick={handlePrevPage}
                  disabled={currentPage <= 1}
                >
                  Previous
                </button>
                <span className="open-ended-page-indicator">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="open-ended-see-more"
                  onClick={handleNextPage}
                  disabled={loadingMore || (!hasMore && currentPage >= totalPages)}
                >
                  {loadingMore ? 'Loading…' : (hasMore && currentPage >= totalPages ? 'Load more' : 'Next')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-gray-500">No responses available.</p>
        )}
      </div>
    </div>
  );
};

export default WordCloudViewer;
